import { useState } from "react";
import { Brain, ChevronDown, ChevronRight, Zap } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import Typography from "@/components/ui/typography";
import { cn } from "@/lib/utils";

interface ThinkingBoxProps {
  thinking: string;
  isStreaming?: boolean;
  hasStartedStreaming?: boolean;
}

export const ThinkingBox = ({
  thinking,
  isStreaming = false,
  hasStartedStreaming = false,
}: ThinkingBoxProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't render if there's no thinking and we're not streaming
  if (!thinking && !isStreaming) return null;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Determine the state: waiting for response, or thinking
  const isWaitingForResponse = isStreaming && !hasStartedStreaming;

  return (
    <div className="border border-sds-blue-40 rounded-lg bg-sds-blue-20 overflow-hidden">
      {/* Header - clickable to expand/collapse */}
      <button
        onClick={handleToggle}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2",
          "hover:bg-sds-blue-40/30 transition-colors",
          "focus:outline-none",
        )}
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-sds-blue-100 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-sds-blue-100 flex-shrink-0" />
        )}
        {isWaitingForResponse ? (
          <Zap className="w-4 h-4 text-sds-blue-100 flex-shrink-0 animate-pulse" />
        ) : (
          <Brain
            className={cn(
              "w-4 h-4 text-sds-blue-100 flex-shrink-0",
              isStreaming && "animate-pulse",
            )}
          />
        )}
        <Typography
          size="l3"
          className="text-sds-blue-100 font-medium text-left flex-1"
        >
          {isWaitingForResponse
            ? "Sending request"
            : isExpanded
              ? "Hide thinking process"
              : "Show thinking process"}
        </Typography>
        {/* Loading indicator when streaming (always visible) */}
        {isStreaming && (
          <div className="flex gap-1">
            <div
              className="w-1.5 h-1.5 rounded-full bg-sds-blue-100 animate-bounce"
              style={{ animationDelay: "0ms" }}
            />
            <div
              className="w-1.5 h-1.5 rounded-full bg-sds-blue-100 animate-bounce"
              style={{ animationDelay: "150ms" }}
            />
            <div
              className="w-1.5 h-1.5 rounded-full bg-sds-blue-100 animate-bounce"
              style={{ animationDelay: "300ms" }}
            />
          </div>
        )}
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-1 border-t border-sds-blue-40">
          {isWaitingForResponse ? (
            <div className="text-sds-neutral-60 text-sm italic">
              Sending request to AI...
            </div>
          ) : thinking ? (
            <div
              className={cn(
                "prose prose-sm max-w-none text-sds-neutral-100",
                // Markdown styles
                "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
                "[&_p]:my-2 [&_p]:leading-relaxed [&_p]:text-sm",
                "[&_strong]:font-semibold [&_strong]:text-sds-text-black",
                "[&_ul]:my-2 [&_ul]:ml-4 [&_ul]:list-disc",
                "[&_ol]:my-2 [&_ol]:ml-4 [&_ol]:list-decimal",
                "[&_li]:my-1 [&_li]:text-sm",
                "[&_h1]:text-base [&_h1]:font-semibold [&_h1]:my-2",
                "[&_h2]:text-sm [&_h2]:font-semibold [&_h2]:my-2",
                "[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:my-1",
                "[&_code]:bg-sds-blue-20 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs",
                "[&_pre]:bg-sds-blue-20 [&_pre]:p-2 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:text-xs",
                // Table styles
                "[&_table]:border-collapse [&_table]:my-3 [&_table]:w-full",
                "[&_table]:text-xs",
                "[&_thead]:bg-sds-blue-40/30",
                "[&_th]:border [&_th]:border-sds-blue-40 [&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold",
                "[&_td]:border [&_td]:border-sds-blue-40 [&_td]:px-2 [&_td]:py-1.5",
                "[&_tbody_tr:hover]:bg-sds-blue-40/20",
              )}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{thinking}</ReactMarkdown>
            </div>
          ) : (
            <div className="text-sds-neutral-60 text-sm italic">
              Waiting for thinking process...
            </div>
          )}
        </div>
      )}
    </div>
  );
};
